import {AsyncInlineError, streamify} from '@s-ui/decorators'
import {UseCase} from '@s-ui/domain'

import {ContactByMailService} from '../../user/Services/ContactByMailService.js'
import {SellerErrorsFactory} from '../Errors/factory.js'
import {ContactSellerRequest} from '../Requests/ContactSellerRequest.js'

@streamify('execute')
class ContactSellerUseCase extends UseCase {
  #config
  #service
  #genericSellerErrorFactory
  #logger

  constructor({config, service, genericSellerErrorFactory, logger}) {
    super({config, service, genericSellerErrorFactory})

    this.#config = config
    this.#service = service
    this.#genericSellerErrorFactory = genericSellerErrorFactory
    this.#logger = logger
  }

  static create({config, logger}) {
    return new ContactSellerUseCase({
      config,
      service: ContactByMailService.create({config}),
      genericSellerErrorFactory: SellerErrorsFactory.genericSellerError,
      logger
    })
  }

  @AsyncInlineError()
  async execute(params) {
    return this.#logger.trace('ContactSellerUseCase#execute', () => this.run(params), {
      tags: [{key: 'usecase', value: 'ContactSellerUseCase'}],
      logErrors: true
    })()
  }

  async run({
    adId,
    captchaToken,
    context,
    contractId,
    email,
    hasAcceptedTermsAndConditions,
    message,
    name,
    phone,
    zipCode,
    buyerIntentions,
    isDefaultMessage,
    subscriptionCommitment,
    isPostFavoriteLead,
    skipSaveContactDetails
  }) {
    const contactSellerRequest = ContactSellerRequest.create({
      adId,
      captchaToken,
      config: this.#config,
      contractId,
      email,
      hasAcceptedTermsAndConditions,
      message,
      name,
      phone,
      zipCode,
      buyerIntentions,
      isDefaultMessage,
      subscriptionCommitment
    })

    const [contactError, contactResponse] = await this.#service.execute({
      adId: contactSellerRequest.getAdId()?.value,
      captchaToken: contactSellerRequest.getCaptchaToken(),
      context,
      contractId: contactSellerRequest.getContractId(),
      message: contactSellerRequest.getMessage(),
      buyer: {
        name: contactSellerRequest.getName(),
        phone: contactSellerRequest.getPhone(),
        email: contactSellerRequest.getEmail(),
        zipCode: contactSellerRequest.getZipCode()
      },
      enrichment: contactSellerRequest.getEnrichment(),
      preferences: contactSellerRequest.getPreferences(),
      isPostFavoriteLead,
      skipSaveContactDetails
    })

    if (contactError) {
      return Promise.reject(this.#genericSellerErrorFactory(contactError.toJSON().error))
    }

    return contactResponse.toJSON()
  }
}

export const factory = ContactSellerUseCase.create
